import React, { useState, useEffect } from 'react';
import { Video, Loader2, Play, Clock, Sparkles, Download, AlertCircle, Image as ImageIcon, Coins, Zap, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogTrigger } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';
import api from '../../utils/api';

// Progress steps for video generation (realistic for AI video gen)
const PROGRESS_LABELS = {
  starting: 'Starting video generation...',
  processing: 'AI is creating your video...',
  completed: 'Video ready!',
  failed: 'Generation failed'
};

// Credit costs per quality tier
const CREDIT_COSTS = {
  standard: 5,
  pro: 15,
  premium: 50
};

const VideoGenerator = ({ videos, onGenerate, isGenerating, generationStatus, fromImage, sourceImageUrl, suggestedPrompt }) => {
  // Initialize prompt with suggestedPrompt if available
  const [prompt, setPrompt] = useState(suggestedPrompt || '');
  const [duration, setDuration] = useState('5s');
  const [quality, setQuality] = useState('standard');
  const [progress, setProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [creditInfo, setCreditInfo] = useState(null);

  // Fetch credit balance on mount
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await api.get('/api/subscription/credits');
        setCreditInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    };
    fetchCredits();
  }, [videos.length]); // Refetch when videos change

  // Get credit cost for selected quality
  const getCreditCost = () => {
    return CREDIT_COSTS[quality] || 5;
  };

  // Quality tiers - All use MiniMax Video-01
  const qualityTiers = [
    { id: 'standard', name: 'Standard', description: 'MiniMax - 5 seconds', badge: 'Basic', color: 'zinc', credits: 10 },
    { id: 'pro', name: 'Pro', description: 'MiniMax - High quality', badge: 'Pro', color: 'blue', credits: 15 },
    { id: 'premium', name: 'Premium', description: 'MiniMax - Best quality', badge: 'Best', color: 'purple', credits: 20 },
  ];

  // Check if tier is allowed
  const isTierAllowed = (tierId) => {
    if (!creditInfo) return true;
    return creditInfo.allowed_tiers?.includes(tierId);
  };

  // Check if user has enough credits
  const hasEnoughCredits = () => {
    if (!creditInfo) return true;
    return creditInfo.balance >= getCreditCost();
  };

  // Progress animation while generating
  useEffect(() => {
    let progressInterval;
    let timeInterval;
    
    if (isGenerating) {
      // Reset progress when generation starts
      const resetProgress = () => {
        setProgress(5);
        setElapsedTime(0);
      };
      resetProgress();
      
      // Slowly increment progress (video gen takes 2-5 mins)
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return prev;
          // Slow down as we get closer to completion
          const increment = prev < 30 ? 2 : prev < 60 ? 1 : 0.5;
          return Math.min(95, prev + increment);
        });
      }, 3000);
      
      // Track elapsed time
      timeInterval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      
    } else if (generationStatus === 'completed') {
      setProgress(100);
    }
    
    return () => {
      if (progressInterval) clearInterval(progressInterval);
      if (timeInterval) clearInterval(timeInterval);
    };
  }, [isGenerating, generationStatus]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (prompt.trim() && !isGenerating) {
      // Pass the source image if available (for image-to-video) and quality
      onGenerate(prompt.trim(), duration, fromImage ? sourceImageUrl : null, quality);
      setPrompt('');
    }
  };

  const suggestions = fromImage ? [
    'Smooth camera pan across the scene',
    'Cinematic walkthrough with dynamic lighting',
    'Dramatic reveal with slow zoom'
  ] : [
    'A hummingbird drinking nectar from a colorful flower',
    'Ocean waves crashing on a tropical beach at golden sunset',
    'Northern lights dancing over a snowy mountain landscape',
    'A majestic eagle soaring through misty mountain peaks'
  ];

  const durations = ['5s', '10s'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'processing': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-800 p-4">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <Video className="h-5 w-5 text-orange-400" />
          Video Studio
          <Badge variant="secondary" className="ml-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-400 border-orange-500/30">
            Powered by AI
          </Badge>
        </h1>
        <p className="text-sm text-zinc-400 mt-1">
          {fromImage ? 'Create a cinematic video from your design' : 'Create stunning AI-powered videos from text descriptions'}
        </p>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Generator Panel */}
        <div className="lg:w-1/2 p-4 border-b lg:border-b-0 lg:border-r border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Source Image Preview (if coming from Design Studio) */}
            {fromImage && sourceImageUrl && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <div className="flex items-center gap-3">
                  <img 
                    src={sourceImageUrl} 
                    alt="Source design" 
                    className="w-20 h-14 object-cover rounded-lg"
                  />
                  <div>
                    <p className="text-sm text-purple-300 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Creating video from your design
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                      AI will animate your architectural visualization
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-zinc-300 block mb-2">
                {fromImage ? 'Describe the video scene' : 'Describe your video'}
              </label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={fromImage 
                  ? "Smooth camera walkthrough with golden hour lighting, cinematic movement..."
                  : "A serene forest landscape with morning mist and birds flying..."}
                className="min-h-[120px] bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 resize-none focus:ring-orange-500 focus:border-orange-500"
                disabled={isGenerating}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-zinc-300 block mb-2">
                Video Duration
              </label>
              <div className="flex gap-2">
                {durations.map((d) => (
                  <Button
                    key={d}
                    type="button"
                    variant={duration === d ? 'default' : 'outline'}
                    disabled={isGenerating}
                    className={cn(
                      "flex-1",
                      duration === d 
                        ? 'bg-orange-600 hover:bg-orange-500 text-white' 
                        : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                    )}
                    onClick={() => setDuration(d)}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    {d}
                  </Button>
                ))}
              </div>
            </div>

            {/* Quality Tier Selection */}
            <div>
              <label className="text-sm font-medium text-zinc-300 block mb-2">
                Video Quality
              </label>
              <div className="grid grid-cols-3 gap-2">
                {qualityTiers.map((tier) => {
                  const isAllowed = isTierAllowed(tier.id);
                  return (
                    <button
                      key={tier.id}
                      type="button"
                      disabled={isGenerating || !isAllowed}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all relative",
                        !isAllowed && "opacity-50 cursor-not-allowed",
                        quality === tier.id
                          ? tier.id === 'standard' 
                            ? 'border-zinc-500 bg-zinc-500/20'
                            : tier.id === 'pro'
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-purple-500 bg-purple-500/20'
                          : 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                      )}
                      onClick={() => isAllowed && setQuality(tier.id)}
                    >
                      {!isAllowed && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                          <Lock className="h-3 w-3 mr-1 text-orange-400" />
                          <span className="text-xs text-orange-400">Upgrade</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn(
                          "text-sm font-medium",
                          quality === tier.id ? 'text-white' : 'text-zinc-300'
                        )}>{tier.name}</span>
                        <span className={cn(
                          "text-xs px-1.5 py-0.5 rounded",
                          tier.id === 'standard' ? 'bg-zinc-700 text-zinc-300' :
                          tier.id === 'pro' ? 'bg-blue-600 text-white' :
                          'bg-purple-600 text-white'
                        )}>{tier.badge}</span>
                      </div>
                      <p className="text-xs text-zinc-500">{tier.description}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <Coins className="h-3 w-3 text-yellow-400" />
                        <span className="text-xs text-yellow-400">{tier.credits} credits</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Credit Balance & Cost Display */}
            <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Coins className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm text-zinc-300">Credit Balance</span>
                </div>
                {creditInfo && (
                  <span className={cn(
                    "text-sm font-medium",
                    creditInfo.balance >= getCreditCost() ? "text-yellow-400" : "text-red-400"
                  )}>
                    {creditInfo.balance} credits
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-cyan-400" />
                  <span className="text-sm text-zinc-300">This generation</span>
                </div>
                <span className="text-sm font-medium text-cyan-400">
                  {getCreditCost()} credits
                </span>
              </div>
              {creditInfo && creditInfo.balance < getCreditCost() && (
                <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-center gap-2">
                  <AlertCircle className="h-3 w-3" />
                  Not enough credits. Buy more to continue.
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={!prompt.trim() || isGenerating || !hasEnoughCredits()}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-medium h-12 disabled:opacity-50"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Video...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  {fromImage ? 'Create Scene Video' : 'Generate Video'}
                </>
              )}
            </Button>

            {/* Progress Bar - Real Status */}
            {isGenerating && (
              <div className="space-y-3 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 animate-in fade-in duration-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
                    <span className="text-sm font-medium text-orange-400">
                      {PROGRESS_LABELS[generationStatus] || 'Processing...'}
                    </span>
                  </div>
                  <span className="text-xs text-zinc-500">
                    Elapsed: {formatTime(elapsedTime)}
                  </span>
                </div>
                
                <Progress value={progress} className="h-2 bg-zinc-800" />
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">
                    Video generation typically takes 2-5 minutes
                  </span>
                  <span className="text-zinc-500">{Math.round(progress)}%</span>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-300 flex items-start gap-2">
                <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  AI video generation creates unique videos from your descriptions. 
                  Be descriptive for best results!
                </span>
              </p>
            </div>
          </form>

          {/* Suggestions */}
          <div className="mt-6">
            <p className="text-sm text-zinc-400 mb-3">Try these prompts:</p>
            <div className="grid grid-cols-1 gap-2">
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => !isGenerating && setPrompt(suggestion)}
                  disabled={isGenerating}
                  className="text-left p-3 text-sm text-zinc-300 bg-zinc-900 hover:bg-zinc-800 rounded-lg border border-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Panel */}
        <div className="lg:w-1/2 flex flex-col min-h-0">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-medium text-white">Your Videos</h2>
            <p className="text-sm text-zinc-400">{videos.length} videos generated</p>
          </div>

          <ScrollArea className="flex-1 p-4">
            {videos.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="w-16 h-16 rounded-2xl bg-zinc-900 flex items-center justify-center mb-4">
                  <Video className="h-8 w-8 text-zinc-600" />
                </div>
                <p className="text-zinc-400">No videos yet</p>
                <p className="text-sm text-zinc-500">Generate your first video above</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    className="group relative rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800"
                  >
                    <div className="aspect-video relative bg-zinc-800">
                      {video.status === 'processing' ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <Loader2 className="h-10 w-10 text-orange-400 animate-spin mb-3" />
                          <p className="text-sm text-zinc-400">Generating video...</p>
                          <p className="text-xs text-zinc-500 mt-1">This may take a few minutes</p>
                        </div>
                      ) : video.status === 'failed' ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
                          <p className="text-sm text-red-400">Generation failed</p>
                        </div>
                      ) : video.videoUrl ? (
                        <>
                          {video.thumbnailUrl ? (
                            <img
                              src={video.thumbnailUrl}
                              alt={video.prompt}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-600/20 to-red-600/20 flex items-center justify-center">
                              <Video className="h-12 w-12 text-orange-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="icon"
                                  className="h-14 w-14 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm"
                                >
                                  <Play className="h-6 w-6 text-white" fill="white" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-2">
                                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                  <video
                                    src={video.videoUrl}
                                    controls
                                    autoPlay
                                    className="w-full h-full"
                                  />
                                </div>
                                <div className="p-4">
                                  <p className="text-sm text-zinc-300">{video.prompt}</p>
                                  <a
                                    href={video.videoUrl}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 mt-3 text-sm text-orange-400 hover:text-orange-300"
                                  >
                                    <Download className="h-4 w-4" />
                                    Download Video
                                  </a>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Video className="h-12 w-12 text-zinc-600" />
                        </div>
                      )}
                      
                      <div className="absolute top-2 right-2 flex gap-2">
                        <Badge className={getStatusColor(video.status)}>
                          {video.status}
                        </Badge>
                        {video.duration && (
                          <Badge className="bg-black/60 text-white border-none">
                            {video.duration}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="text-sm text-white line-clamp-2">{video.prompt}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {video.createdAt && new Date(video.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default VideoGenerator;
